import { useMemo, useState } from "react";
import { Link } from "react-router";

import { PageSections, type SectionRenderers } from "@/components/cms/PageSections";
import { MediaImage } from "@/components/ui/MediaImage";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal, RevealGroup } from "@/components/ui/Reveal";
import { Section } from "@/components/ui/Section";
import { useContent, useMediaByKey } from "@/content-context";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

/**
 * News and announcements.
 *
 * Separate from /stories on purpose. A story is field reporting written from a
 * documented event and has a place, a date and a pillar attached. An article is
 * whatever else HISCUS needs to publish: an announcement, a note about a
 * document, a piece of archived material brought forward. Merging the two would
 * mean one of them had to pretend to be the other.
 *
 * Drafts never appear here. The public content service filters them out before
 * the list leaves the API, so a mistake in this template cannot leak one.
 */

export const loader = pageLoader("news");

export const meta = () =>
  buildMetadata({
    title: "News",
    description:
      "Announcements and reporting from Himshikhara Socio-Cultural Society (HISCUS Nepal), newest first.",
    path: "/news",
  });

function ListSection() {
  const { articles } = useContent();
  const byKey = useMediaByKey();
  const [category, setCategory] = useState("All");

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(articles.map((a) => a.category).filter(Boolean)))],
    [articles],
  );

  const visible = category === "All" ? articles : articles.filter((a) => a.category === category);

  return (
    <Section>
      {categories.length > 2 ? (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter news by category">
          {categories.map((name) => {
            const selected = name === category;
            return (
              <button
                key={name}
                type="button"
                aria-pressed={selected}
                onClick={() => setCategory(name)}
                className={`inline-flex min-h-11 items-center rounded-full border px-4 text-[0.875rem] font-medium transition-[background-color,border-color,color] duration-300 ${
                  selected
                    ? "border-navy bg-navy text-white"
                    : "border-line bg-white text-ink/70 hover:border-line-strong hover:bg-mist hover:text-ink"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="mt-8 max-w-2xl text-muted">
          Nothing has been published here yet. Reporting from the field is on the{" "}
          <Link to="/stories" className="link-underline font-semibold text-navy">
            stories
          </Link>{" "}
          page.
        </p>
      ) : (
        <RevealGroup as="ul" className="mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((article) => {
            const image = byKey(article.featuredMediaKey);
            return (
              <Reveal as="li" key={article.slug}>
                <article className="surface group relative flex h-full flex-col overflow-hidden rounded-[16px]">
                  {image ? (
                    <div className="relative aspect-[16/10] w-full overflow-hidden bg-mist">
                      <MediaImage
                        item={image}
                        fill
                        sizes="(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 100vw"
                        className="object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
                      />
                    </div>
                  ) : null}

                  <div className="flex flex-1 flex-col p-6">
                    <p className="eyebrow">{article.category}</p>
                    <h2 className="mt-3" style={{ fontSize: "var(--step-h4)" }}>
                      <Link to={`/news/${article.slug}`}>
                        <span className="after:absolute after:inset-0 after:content-['']">{article.title}</span>
                      </Link>
                    </h2>
                    <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{article.excerpt}</p>
                    <p className="mt-auto pt-5 text-[0.8125rem] text-muted">
                      <time dateTime={article.isoDate}>{article.displayDate || formatDate(article.isoDate)}</time>
                      {article.readingTime ? ` · ${article.readingTime}` : ""}
                    </p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </RevealGroup>
      )}
    </Section>
  );
}

export const newsSections: SectionRenderers = {
  "news.list": ListSection,
};

export function NewsPage() {
  return (
    <>
      <PageHeader
        eyebrow="News"
        title="Announcements and reporting"
        lead="Everything HISCUS has published here, newest first. Each piece carries the date it refers to and, where one exists, the source it came from."
        trail={[
          { name: "Home", href: "/" },
          { name: "News", href: "/news" },
        ]}
      />
      <PageSections renderers={newsSections} />
    </>
  );
}

export function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
